'use client';
import React, { useState } from 'react';
import {
  AvatarGroup,
  Avatar as MUIAvatar,
  Chip,
  Button,
  Typography,
  Box,
  Grid2 as Grid,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Paper,
  Avatar,
} from '@mui/material';
import { CancelRounded, ListRounded } from '@mui/icons-material';
import { CourseData, DetailEduPathProps } from '@/shared/types';
import InfoCard from 'src/shared/ui/InfoCard';
import { NumberOfLessons } from './number-of-lessons';
import { EventBus } from '@/shared/utils';
import { LearningPathByIdEventKeys, LearningPathByIdEvents } from '../../events';
import { AddToFavourites } from './favourites';
import { LevelIconSelector } from 'src/modules/page/utils/select-level-icon';
import { toPascalCase } from 'src/shared/utils/pascal-case';
import { MarkdownRenderer } from 'src/shared/components/markdown-renderer';

// Utility function for instructor names
const getInstructorName = (instructors: CourseData['instructors']) => {
  if (instructors.length === 1) {
    return instructors[0].name;
  } else if (instructors.length > 1) {
    return `${instructors.length} Insegnanti`;
  }
  return '';
};

const EducationalPathDetail: React.FC<
  DetailEduPathProps & {
    eventBus: EventBus<LearningPathByIdEvents>;
    learningPathDocumentId: string;
  }
> = ({
  title,
  level,
  instructors = [],
  learningPathDocumentId,
  description,
  eventBus,
  courseType,
  isSubscriber,
  requirements,
  thumbnail,
}) => {
  // State for handling the instructor dialog
  const [open, setOpen] = useState(false);

  // Function to handle opening the dialog
  const handleOpen = () => {
    setOpen(true);
  };

  // Function to handle closing the dialog
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Grid container spacing={{ xs: 1, md: 2 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Box
            component="img"
            src={thumbnail || '/assets/images/placeholder/card-placeholder-2.png'}
            alt={`Cover for ${title} course`}
            loading="lazy"
            className="w-full h-[270px] md:h-[468px] mb-4 rounded-xl md:rounded-3xl"
            sx={{
              objectFit: 'cover',
            }}
          />
          <Grid container className="w-full" spacing={{ xs: 1, md: 2 }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <InfoCard title="Livello" className="h-full rounded-2xl">
                <Chip
                  label={<Typography variant="button-s">{toPascalCase(level)}</Typography>}
                  size="medium"
                  variant="filled"
                  className="bg-neutral-main"
                  avatar={
                    <Avatar
                      src={LevelIconSelector(level, courseType, false)}
                      className="mr-0 w-5 h-auto"
                    />
                  }
                />
              </InfoCard>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <InfoCard title="Lezioni" className="h-full rounded-2xl">
                <Typography variant="body1">
                  <NumberOfLessons />
                </Typography>
              </InfoCard>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <InfoCard
                title="Insegnanti"
                className="h-full rounded-2xl"
                button={
                  Array.isArray(instructors) &&
                  instructors.length > 1 && (
                    <Button
                      size="small"
                      className="gap-1"
                      variant="text"
                      sx={{ padding: '0 !important' }}
                      onClick={handleOpen}
                    >
                      <ListRounded />
                      <Typography variant="button-s" className="p-0">
                        Mostra tutti
                      </Typography>
                    </Button>
                  )
                }
              >
                <Box display="flex" alignItems="center">
                  <AvatarGroup max={3}>
                    {Array.isArray(instructors) && instructors.length > 0 ? (
                      instructors.map((instructor, index) => (
                        <MUIAvatar
                          key={index}
                          src={instructor.avatarUrl}
                          alt={`${instructor.name}'s avatar`}
                          sx={{
                            height: 32,
                            width: 32,
                            borderColor: '#15151A',
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2">Nessun insegnante trovato</Typography>
                    )}
                  </AvatarGroup>
                  <Typography variant="body2" ml={1}>
                    {getInstructorName(instructors)}
                  </Typography>
                </Box>
              </InfoCard>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={2} className="h-full">
            <Paper
              elevation={3}
              sx={{ p: 3, borderRadius: 4 }}
              className="gap-6 bg-paper flex-1 md:max-h-[468px] overflow-auto"
            >
              {(description || requirements) && (
                <Stack spacing={3}>
                  {description && (
                    <Typography variant="body1">
                      <MarkdownRenderer markdown={description} />
                    </Typography>
                  )}

                  {requirements && (
                    <Stack spacing={1}>
                      <Typography variant="s1" color="textSecondary">
                        Requisiti
                      </Typography>
                      <Typography variant="body1">{requirements}</Typography>
                    </Stack>
                  )}
                </Stack>
              )}
            </Paper>
            <div className="space-y-2">
              <Button
                onClick={() =>
                  eventBus.emit(
                    LearningPathByIdEventKeys.redirects.to,
                    isSubscriber ? '/my-home' : '/abbonati',
                  )
                }
                variant="contained"
                color="primary"
                fullWidth
                size="large"
              >
                <Typography variant="button-l">
                  {isSubscriber ? 'La mia Home' : 'Accedi a tutti i corsi'}
                </Typography>
              </Button>
              <AddToFavourites
                eventBus={eventBus}
                learningPathDocumentId={learningPathDocumentId}
              />
            </div>
          </Stack>
        </Grid>
      </Grid>

      {/* Dialog for showing instructors */}
      <Dialog open={open} onClose={handleClose} fullWidth>
        <DialogTitle className="flex items-center justify-between p-6">
          Lista Insegnanti
          <IconButton aria-label="close" onClick={handleClose}>
            <CancelRounded className="text-action-divider" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {instructors.length > 0 ? (
            <Stack spacing={1}>
              {instructors.map((instructor, index) => (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  className="p-6 bg-background"
                  borderRadius={3}
                >
                  <MUIAvatar
                    src={instructor.avatarUrl}
                    alt={`${instructor.name}'s avatar`}
                    sx={{ marginRight: 1 }}
                  />
                  <div className="flex flex-col">
                    <Typography variant="s2" className="leading-none">
                      {instructor.name}
                    </Typography>
                    {!!instructor?.artistsWorkedwith?.artists?.length && (
                      <div className="flex flex-col gap-2 my-2">
                        <Typography>
                          <span className="text-text-secondary font-semibold">
                            Artisti con cui ha suonato:
                          </span>{' '}
                          {instructor?.artistsWorkedwith?.artists
                            ?.map((artist) => `${artist?.name || ''} ${artist?.surname || ''}`)
                            .join(', ')}
                        </Typography>
                      </div>
                    )}
                  </div>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography>Nessun insegnante disponibile</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EducationalPathDetail;
